import React, { useState, useEffect } from 'react';
import { Lock, Package, User, Mail, Phone, MapPin, Calendar, CreditCard, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = '/api';

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    document.title = 'Admin - Salt & Stone';
  }, []);

  // Check if already authenticated from session
  useEffect(() => {
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch orders when authenticated
  useEffect(() => {
    if (isAuthenticated && password) {
      fetchOrders();
    }
  }, [isAuthenticated, password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminPassword', password);
      }
    } catch (err) {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders?password=${encodeURIComponent(password)}`);
      setOrders(response.data.orders || []);
    } catch (err) {
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (sessionId) => {
    setSelectedOrder(sessionId);
    setOrderDetails(null);
    try {
      const response = await axios.get(`${API}/admin/order/${sessionId}?password=${encodeURIComponent(password)}`);
      setOrderDetails(response.data);
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl text-[#F2F0E9] mb-2">Admin Access</h1>
            <p className="text-[#F2F0E9]/60 text-sm">Enter password to view orders</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-transparent border border-white/20 text-[#F2F0E9] px-4 py-3 focus:border-[#D4AF37] outline-none transition-colors"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="admin-login-btn"
            >
              {loading ? 'Verifying...' : 'Access Orders'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Orders Dashboard
  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16 px-6" data-testid="admin-dashboard">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl text-[#F2F0E9] mb-2">Orders Dashboard</h1>
            <p className="text-[#F2F0E9]/60 text-sm">{orders.length} order(s) found</p>
          </div>
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="flex items-center gap-2 text-[#D4AF37] hover:text-[#F2F0E9] transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orders List */}
          <div className="space-y-4">
            <h2 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">All Orders</h2>
            
            {loading && orders.length === 0 ? (
              <div className="text-[#F2F0E9]/60 text-center py-8">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="text-[#F2F0E9]/60 text-center py-8">No orders yet</div>
            ) : (
              orders.map((order) => (
                <div
                  key={order.session_id}
                  onClick={() => fetchOrderDetails(order.session_id)}
                  className={`p-4 border cursor-pointer transition-all ${
                    selectedOrder === order.session_id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-[#F2F0E9] font-medium">
                        {formatCurrency(order.amount)}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      order.payment_status === 'paid' 
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {order.payment_status || 'pending'}
                    </span>
                  </div>
                  
                  <div className="text-[#F2F0E9]/60 text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      {formatDate(order.created_at)}
                    </div>
                    <div className="text-xs text-[#F2F0E9]/40 truncate">
                      ID: {order.session_id?.slice(-12)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Order Details */}
          <div className="lg:sticky lg:top-24">
            <h2 className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Order Details</h2>
            
            {!selectedOrder ? (
              <div className="border border-white/10 p-8 text-center">
                <Package className="w-12 h-12 text-[#F2F0E9]/20 mx-auto mb-4" />
                <p className="text-[#F2F0E9]/40">Select an order to view details</p>
              </div>
            ) : !orderDetails ? (
              <div className="border border-white/10 p-8 text-center">
                <RefreshCw className="w-8 h-8 text-[#D4AF37] mx-auto mb-4 animate-spin" />
                <p className="text-[#F2F0E9]/60">Loading details...</p>
              </div>
            ) : (
              <div className="border border-white/10 p-6 space-y-6">
                {/* Customer Info */}
                <div>
                  <h3 className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Customer</h3>
                  <div className="space-y-2">
                    {orderDetails.stripe_data?.customer_name && (
                      <div className="flex items-center gap-2 text-[#F2F0E9]">
                        <User className="w-4 h-4 text-[#F2F0E9]/40" />
                        {orderDetails.stripe_data.customer_name}
                      </div>
                    )}
                    {orderDetails.stripe_data?.customer_email && (
                      <div className="flex items-center gap-2 text-[#F2F0E9]">
                        <Mail className="w-4 h-4 text-[#F2F0E9]/40" />
                        {orderDetails.stripe_data.customer_email}
                      </div>
                    )}
                    {orderDetails.stripe_data?.customer_phone && (
                      <div className="flex items-center gap-2 text-[#F2F0E9]">
                        <Phone className="w-4 h-4 text-[#F2F0E9]/40" />
                        {orderDetails.stripe_data.customer_phone}
                      </div>
                    )}
                  </div>
                </div>

                {/* Shipping Address */}
                {orderDetails.stripe_data?.shipping_address && (
                  <div>
                    <h3 className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Shipping Address</h3>
                    <div className="flex items-start gap-2 text-[#F2F0E9]">
                      <MapPin className="w-4 h-4 text-[#F2F0E9]/40 mt-1" />
                      <div className="text-sm">
                        {orderDetails.stripe_data.shipping_address.line1 && (
                          <div>{orderDetails.stripe_data.shipping_address.line1}</div>
                        )}
                        {orderDetails.stripe_data.shipping_address.line2 && (
                          <div>{orderDetails.stripe_data.shipping_address.line2}</div>
                        )}
                        <div>
                          {orderDetails.stripe_data.shipping_address.city}{' '}
                          {orderDetails.stripe_data.shipping_address.postal_code}
                        </div>
                        <div>{orderDetails.stripe_data.shipping_address.country}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Summary */}
                <div>
                  <h3 className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Order Summary</h3>
                  <div className="space-y-2">
                    {orderDetails.stripe_data?.line_items?.map((item, index) => (
                      <div key={index} className="flex justify-between text-[#F2F0E9] text-sm">
                        <span>{item.description} x{item.quantity}</span>
                        <span>{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                    <div className="border-t border-white/10 pt-2 mt-2">
                      <div className="flex justify-between text-[#F2F0E9] font-medium">
                        <span>Total</span>
                        <span>{formatCurrency(orderDetails.stripe_data?.amount_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Status */}
                <div>
                  <h3 className="text-[#D4AF37] text-xs uppercase tracking-widest mb-3">Payment</h3>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#F2F0E9]/40" />
                    <span className={`text-sm ${
                      orderDetails.stripe_data?.payment_status === 'paid'
                        ? 'text-green-400'
                        : 'text-yellow-400'
                    }`}>
                      {orderDetails.stripe_data?.payment_status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
